import { motion } from "framer-motion";
import { Presenter, PresenterBubble } from "./Presenter";

/**
 * The Stage
 * =========
 * A cinematic backdrop that moves together with the Presenter. Both live inside
 * a single transformed container so camera moves feel like real pan/zoom — not
 * two separate layers sliding independently.
 *
 * Camera presets are declared once and referenced by name from the rooms.
 * When a preset changes, framer-motion animates the container's `scale` +
 * `x/y` translation over ~1.2s with a soft ease.
 *
 * Coordinates are expressed as percentages of the stage box, so the same
 * presets work responsively at any viewport size.
 *
 *   preset.scale     — zoom factor, 1 = wide shot
 *   preset.x, preset.y — background translate (%) to shift the "camera"
 *   preset.presenter — where the Presenter stands on the stage (%)
 *   preset.presenterScale — presenter scale multiplier
 *   preset.bubble    — viewport-relative bubble anchor (top/left %, width px)
 */

const STAGE_ASPECT = 1672 / 941; // matches the room-stage.jpg
const STAGE_IMG = "/audit/room-stage.jpg";

export const CAMERA_PRESETS = {
  wide: {
    scale: 1,
    x: 0,
    y: 0,
    presenter: { xPct: 50, yPct: 78 },
    presenterScale: 1,
    bubble: { topPct: 30, leftPct: 62, widthPx: 320 },
  },
  whiteboard: {
    // Reference-matched: whiteboard fills the frame, NO floor visible,
    // presenter stands to the left cropped at the thighs (feet below frame).
    // Whiteboard in source lives at ~(40-76%, 17-51%). Camera math:
    //   on-screen = 50/55 + (pos - 50/55) * scale + shift.
    // scale 2.0, x=-10, y=41 places whiteboard at ~(20-92%, 20-88%) with
    // the floor pushed off-screen (floor edge lands at ~101% vertically).
    scale: 2.0,
    x: -10,
    y: 41,
    presenter: { xPct: 36, yPct: 67 },
    presenterScale: 1.2,
    // Bubble sits above the presenter's head, tail pointing to mouth. The
    // bubble is fixed to the viewport so it stays readable and never
    // overlaps the whiteboard notes (which start around x=30% viewport).
    bubble: { topPct: 6, leftPct: 14, widthPx: 300 },
  },
  floor: {
    // Push down + slightly closer to reveal the floor grid
    scale: 1.2,
    x: 0,
    y: -10,
    presenter: { xPct: 50, yPct: 82 },
    presenterScale: 1.05,
    bubble: { topPct: 24, leftPct: 60, widthPx: 320 },
  },
  desk: {
    // Pan right + zoom in toward the desk / bookcase corner
    scale: 1.55,
    x: -22,
    y: -4,
    presenter: { xPct: 74, yPct: 86 },
    presenterScale: 0.9,
    bubble: { topPct: 18, leftPct: 30, widthPx: 320 },
  },
};

const CAMERA_TRANSITION = {
  duration: 1.25,
  ease: [0.22, 1, 0.36, 1], // premium ease-out
};

// ----------------------------------------------------------------------------
// Floor shadow — a subtle radial ellipse anchored to the presenter's feet.
// ----------------------------------------------------------------------------
const FloorShadow = ({ scale = 1 }) => (
  <div
    aria-hidden="true"
    className="absolute left-1/2 -translate-x-1/2"
    style={{
      bottom: `${-6 * scale}px`,
      width: `${170 * scale}px`,
      height: `${28 * scale}px`,
      background:
        "radial-gradient(ellipse at center, rgba(20,15,10,0.38) 0%, rgba(20,15,10,0.18) 45%, rgba(20,15,10,0) 75%)",
      filter: "blur(4px)",
    }}
  />
);

// ----------------------------------------------------------------------------
// Stage
// ----------------------------------------------------------------------------
export function Stage({
  camera = "wide",
  pose = "neutral",
  message = "",
  background,         // optional per-room override; falls back to STAGE_IMG
  children,           // rendered as UI overlays above the stage (progress, panels, nav)
  boardOverlay,       // rendered INSIDE the camera transform — moves with the room
  testId = "stage",
}) {
  const preset = CAMERA_PRESETS[camera] || CAMERA_PRESETS.wide;
  const bgImg = background || STAGE_IMG;

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#141310]" data-testid={testId}>
      {/* Warm room ambient tint under the image */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,#3a2a1c_0%,#181310_75%)]" aria-hidden="true" />

      {/* Camera container — background + presenter move together */}
      <motion.div
        className="absolute inset-0"
        animate={{
          scale: preset.scale,
          x: `${preset.x}%`,
          y: `${preset.y}%`,
        }}
        transition={CAMERA_TRANSITION}
        style={{ transformOrigin: "50% 55%" }}
        data-testid={`${testId}-camera`}
      >
        {/* Room backdrop — sized to cover, aspect-locked to the source image */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: "100%",
            height: "100%",
            backgroundImage: `url(${bgImg})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            aspectRatio: STAGE_ASPECT,
            minHeight: "100%",
            minWidth: "100%",
          }}
          data-testid={`${testId}-backdrop`}
        />

        {/* Board overlay — pinned inside the room, transforms with the camera */}
        {boardOverlay ? (
          <div className="absolute inset-0" data-testid="board-overlay-layer">
            {boardOverlay}
          </div>
        ) : null}

        {/* Presenter — placed in the room, moves with the camera */}
        <motion.div
          className="absolute"
          animate={{
            left: `${preset.presenter.xPct}%`,
            top: `${preset.presenter.yPct}%`,
            scale: preset.presenterScale,
          }}
          transition={CAMERA_TRANSITION}
          style={{ translate: "-50% -100%" }}
          data-testid="stage-presenter-anchor"
        >
          <div className="relative">
            <FloorShadow scale={preset.presenterScale} />
            <Presenter
              pose={pose}
              size="md"
              className="items-end"
              testId="stage-presenter"
            />
          </div>
        </motion.div>
      </motion.div>

      {/* Cinematic vignette on top of the stage — keeps focus centred */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.35) 90%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* Speech bubble — viewport-fixed, OUTSIDE the camera transform so it
          never scales with the room zoom and stays out of the notes area. */}
      <PresenterBubble message={message} anchor={preset.bubble} testId="stage-presenter-bubble" />

      {/* UI overlays (progress, room panels, nav) — only opt-in children capture clicks */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 pointer-events-none">{children}</div>
      </div>
    </div>
  );
}

export default Stage;
